import React, { useMemo, useState } from 'react'
import styled from 'styled-components'

import { Box, Icon, Text, Card, Toast, MoreOptionsPopOver, Tooltip } from 'components'

// Utils and Services
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'
import { updateLeadContact } from 'services/lead-contact'
import { useLeadContactQueryClient } from 'queries/lead-contact'
import { HexToRgba } from 'utils/colors'
import { FETCHING_STATUS } from 'utils/constants'

// Types
import { PopOverItem } from 'components/PopOver/types'
import { LeadDetails } from 'types/global'

import { Actions, getMenuOptions } from './helper'
import ViewContactDetails from './ViewContactDetails'
import useScopeModal from 'hooks/use-scope-modal'
import { MODAL_STACK_CONTACT_LIST } from '.'

type BodyComponentProps = {
  disabled: boolean
  data: LeadDetails.ContactPerson
}

const BodyComponent: React.FC<BodyComponentProps> = ({ data, disabled }) => {
  const { contactTypes } = useWorkFlowLookup({})
  const { contactTypeId, email, jobTitle, mobile } = data

  const tag = useMemo(() => {
    const result: string[] = []

    if (jobTitle) {
      result.push(jobTitle)
    }

    const contactType = contactTypes.find((i) => i.id === contactTypeId)

    if (contactType?.name) {
      result.push(contactType.name)
    }

    return result.join(' | ')
  }, [contactTypeId, contactTypes, jobTitle])

  return (
    <Box display="flex" flexDirection="column" gap="4px">
      {tag && (
        <Tag color="green2" lineHeight="18px" p="2px 4px">
          {tag}
        </Tag>
      )}
      {email.map((em, index) => (
        <Box key={index} display="flex" alignItems="center" gap="8px">
          <Icon color={disabled ? 'gray4' : 'blue2'} icon={['far', 'envelope']} />
          <Text lineHeight="24px" color="gray5">
            {em?.value || '-'}
          </Text>
          {em.isPrimary && (
            <Box>
              <Tooltip position="right" title="primary_email">
                <Icon color="gray4" icon={['fas', 'flag']} fontSize="12px" />
              </Tooltip>
            </Box>
          )}
        </Box>
      ))}
      {mobile.map((mb, index) => (
        <Box key={index} display="flex" alignItems="center" gap="8px">
          <Icon color={disabled ? 'gray4' : 'blue2'} icon={['far', 'phone']} />
          <Text lineHeight="24px" color="gray5">
            {mb?.displayValue || '-'}
          </Text>

          {mb.isPrimary && (
            <Box>
              <Tooltip position="right" title="primary_number">
                <Icon color="gray4" fontSize="12px" icon={['fas', 'flag']} />
              </Tooltip>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  )
}

type ContactCardProps = {
  canEdit: boolean
  disabled: boolean
  hideMenu: boolean
  isLastActiveContact: boolean
  leadContact: LeadDetails.ContactPerson
  setConfirmDialog: (mode: keyof typeof Actions) => void
  setSelectedId: (id: string) => void
  isActive?: boolean
}

const ContactCard = ({
  canEdit,
  disabled,
  hideMenu,
  isLastActiveContact,
  leadContact,
  setConfirmDialog,
  setSelectedId,
  isActive,
}: ContactCardProps) => {
  const { invalidateQuery } = useLeadContactQueryClient(leadContact.leadId)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { setModal, setCloseModal } = useScopeModal({ stack: MODAL_STACK_CONTACT_LIST })

  const handleMarkAsPrimary = async (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.stopPropagation()

    setIsSubmitting(true)

    const payload = {
      contact_id: leadContact.id as string,
      contact_type_id: leadContact.contactTypeId,
      email: leadContact.email.map(({ contactInfoTypeId, id, isPrimary, value }: LeadDetails.Email) => ({
        contact_info_id: id,
        contact_info_type_id: contactInfoTypeId,
        is_primary: isPrimary,
        value,
      })),
      fname: leadContact.fname,
      job_title: leadContact.jobTitle,
      lead_id: leadContact.leadId,
      is_primary: true,
      lname: leadContact.lname,
      mobile: leadContact.mobile.map(({ id, contactInfoTypeId, isPrimary, value }: LeadDetails.Mobile) => ({
        contact_info_id: id,
        contact_info_type_id: contactInfoTypeId,
        is_primary: isPrimary,
        value,
      })),
    }

    const response = await updateLeadContact(payload)
    setIsSubmitting(false)

    if (response.status === FETCHING_STATUS.fail) {
      Toast.toastMessage({
        message: `lead_contact_set_primary_error`,
        variant: 'error',
      })
      return
    }

    Toast.toastMessage({
      message: `lead_contact_set_primary_success`,
      variant: 'success',
    })

    invalidateQuery()
  }

  const menuOptions = getMenuOptions(isLastActiveContact, leadContact.isActive, leadContact.isPrimary)

  const handleClickOption = (value?: PopOverItem) => {
    switch (value?.value) {
      case Actions.markAsPrimary:
        handleMarkAsPrimary()
        break
      case Actions.deactivate:
      case Actions.delete:
        setConfirmDialog(value.value)
        setSelectedId(leadContact.id)
        break
      default:
    }
  }

  const isCardActive = !disabled && leadContact.isActive

  const cardTitle = (
    <Box alignItems="center" display="flex" gap="8px">
      {isCardActive && (
        <Icon
          color={leadContact.isPrimary ? 'orange' : 'gray4'}
          fontSize={15}
          icon={leadContact.isPrimary ? 'flag' : ['far', 'flag']}
          onClick={handleMarkAsPrimary}
        />
      )}
      <Text fontWeight="bold">
        {((leadContact.fname || leadContact.lname) && `${leadContact.fname ?? ''} ${leadContact.lname ?? ''}`) || '-'}
      </Text>
    </Box>
  )

  return (
    <>
      <Card
        actionItems={
          <MoreOptionsPopOver
            isLoading={isSubmitting}
            isPortaled={false}
            isVisible={canEdit && !disabled && !hideMenu}
            options={menuOptions}
            onSelect={handleClickOption}
            showTargetBg={false}
            showTargetBorder={false}
            targetSize="small"
          />
        }
        isActive={(canEdit && isCardActive) || isActive === true}
        isClickable={canEdit}
        mb={16}
        onClick={() => {
          setModal(
            <ViewContactDetails
              data={leadContact}
              isLastActiveContact={isLastActiveContact}
              onClose={() => setCloseModal('view-contact-details')}
            />,
            {
              id: 'view-contact-details',
            },
          )
        }}
        title={cardTitle}
      >
        <BodyComponent data={leadContact} disabled={!isCardActive} />
      </Card>
    </>
  )
}

const Count = styled(Text)`
  background-color: ${(p) => p.theme.colors.gray3};
  border: 1px solid ${(p) => p.theme.colors.gray4};
  border-radius: 2px;
`

const Tag = styled(Text)`
  background-color: ${(p) => HexToRgba(p.theme.colors.green1, 0.2)};
  border: 1px solid ${(p) => p.theme.colors.green1};
  border-radius: 4px;
`

export default ContactCard
